/**
 * Document Service
 * Handles document management API calls (WonderVault).
 * Uses the shared apiClient (Axios) for auth, CSRF, and credentials.
 * S3 presigned uploads use a direct fetch (different origin, no auth headers needed).
 */

import apiClient from './apiClient';

class DocumentService {
  async getCategories() {
    try {
      const { data } = await apiClient.get('/api/documents/categories');
      return data;
    } catch (error) {
      console.error('Error fetching document categories:', error);
      throw error;
    }
  }

  // Presigned upload flow (primary method)
  async uploadDocumentPresigned(file, scopeType, category, description, confirmationNumber, tripId = null) {
    try {
      // Step 1: Get presigned upload URL
      const { data: presignData } = await apiClient.post('/api/documents/presign-upload', {
        scopeType,
        fileName: file.name,
        mimeType: file.type,
        category,
        description,
        confirmationNumber,
        tripId: scopeType === 'TRIP' ? tripId : undefined,
        sizeBytes: file.size,
      });

      const { uploadUrl, storageKey } = presignData.data;

      // Step 2: Upload directly to S3 via presigned URL (different origin — raw fetch).
      // Content-Type must exactly match what was used to sign the URL.
      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });

      if (!s3Response.ok) {
        throw new Error(`S3 upload failed: ${s3Response.statusText}`);
      }

      // Step 3: Complete upload (store metadata in database)
      const { data } = await apiClient.post('/api/documents/complete', {
        storageKey,
        scopeType,
        fileName: file.name,
        mimeType: file.type,
        category,
        description,
        confirmationNumber,
        tripId: scopeType === 'TRIP' ? tripId : undefined,
        sizeBytes: file.size,
      });

      return data;
    } catch (error) {
      console.error('Error uploading document (presigned flow):', error);
      throw error;
    }
  }

  // Legacy upload (kept for backward compatibility)
  async uploadDocument(file, category, description, confirmationNumber, tripId = null, clientId = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      if (description) formData.append('description', description);
      if (confirmationNumber) formData.append('confirmationNumber', confirmationNumber);
      if (tripId) formData.append('tripId', tripId);
      if (clientId) formData.append('clientId', clientId);

      // Axios removes Content-Type for FormData so the browser sets the correct
      // multipart/form-data boundary automatically.
      const { data } = await apiClient.post('/api/documents/upload', formData);
      return data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  async getAllDocuments(category = null, scopeType = null, tripId = null) {
    try {
      const params = {};
      if (category) params.category = category;
      if (scopeType) params.scopeType = scopeType;
      if (tripId) params.tripId = tripId;

      const { data } = await apiClient.get('/api/documents', { params });
      return data;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  }

  async getDocuments(tripId, category = null, clientId = null) {
    try {
      const params = {};
      if (category) params.category = category;
      if (clientId) params.clientId = clientId;

      const { data } = await apiClient.get(`/api/documents/trip/${tripId}`, { params });
      return data;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  }

  async getDownloadUrl(documentId, expiresIn = 3600) {
    try {
      const { data } = await apiClient.get(`/api/documents/${documentId}/download`, {
        params: { expiresIn },
      });
      return data;
    } catch (error) {
      console.error('Error getting download URL:', error);
      throw error;
    }
  }

  async updateDocument(documentId, updates) {
    try {
      const { data } = await apiClient.patch(`/api/documents/${documentId}`, updates);
      return data;
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  }

  async deleteDocument(documentId) {
    try {
      const { data } = await apiClient.delete(`/api/documents/${documentId}`);
      return data;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  async downloadDocument(documentId) {
    try {
      const { data } = await this.getDownloadUrl(documentId);
      window.open(data.downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(mimeType) {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    if (mimeType.includes('text')) return '📄';
    return '📎';
  }
}

export default new DocumentService();
